import * as React from 'react';
import type {ChatMessage, ChatToolCall} from '../hooks/chat/types';
import type {ChatSurfaceVariant} from './chat-variant';
import {toolCallLabel, type ToolCallConfigMap} from './tool-call-stack';

// At least the paced reveal's longest lag, so "Thinking..." never shows under text still pouring in.
const TEXT_IDLE_MS = 2500;
/** After this long with no activity, "Thinking..." admits it is taking a while. */
const STILL_WORKING_MS = 30_000;
const NO_TOOL_CALLS: ChatToolCall[] = [];

/** Generic thinking belongs before the turn's first concrete action only. */
export function hasToolActivityInCurrentTurn(messages: ChatMessage[]): boolean {
  for (let index = messages.length - 1; index >= 0; index--) {
    const message = messages[index];
    if (message.role === 'user') {
      return false;
    }
    if ((message.toolCalls?.length ?? 0) > 0) {
      return true;
    }
  }

  return false;
}

/** Keep the row up after a tool completes, until the reply's text takes over. */
export function shouldShowReviewingStatus({
  turnHasToolActivity,
  replyIsStreaming,
  hasReadableText,
  isTextIdle,
}: {
  turnHasToolActivity: boolean;
  replyIsStreaming: boolean;
  hasReadableText: boolean;
  isTextIdle: boolean;
}): boolean {
  return (
    turnHasToolActivity && replyIsStreaming && (!hasReadableText || isTextIdle)
  );
}

export interface UseTurnActivityOptions {
  messages: ChatMessage[];
  isAwaitingResponse: boolean;
  isStreaming?: boolean;
  toolCallConfig?: ToolCallConfigMap;
  variant: ChatSurfaceVariant;
}

export interface TurnActivity {
  turnIsLive: boolean;
  showActivityRow: boolean;
  activityLabel: string;
  liveToolCalls: ChatToolCall[];
}

/** Owns the timers behind the in-flight status row so the component only renders it. */
export function useTurnActivity({
  messages,
  isAwaitingResponse,
  isStreaming,
  toolCallConfig,
  variant,
}: UseTurnActivityOptions): TurnActivity {
  const turnHasToolActivity = hasToolActivityInCurrentTurn(messages);

  // Shorter delay for the first response, longer mid-turn
  const lastMessage = messages[messages.length - 1];
  const thinkingDelay = lastMessage?.role === 'user' ? 0 : 2000;

  const streamingReply = lastMessage?.streaming ? lastMessage : undefined;

  // The last tool still running in the current reply. Tools keep starting
  // after text has begun in a multi-step turn, so this outlives the initial
  // thinking window.
  const activeToolCall = React.useMemo(() => {
    const calls = streamingReply?.toolCalls ?? [];
    for (let i = calls.length - 1; i >= 0; i--) {
      if (calls[i].streaming) {
        return calls[i];
      }
    }
    return undefined;
  }, [streamingReply]);

  const [showThinking, setShowThinking] = React.useState(
    () => isAwaitingResponse && thinkingDelay === 0,
  );
  React.useEffect(() => {
    if (!isAwaitingResponse) {
      setShowThinking(false);
      return;
    }
    if (thinkingDelay === 0) {
      setShowThinking(true);
      return;
    }
    const timer = setTimeout(() => setShowThinking(true), thinkingDelay);
    return () => clearTimeout(timer);
  }, [isAwaitingResponse, thinkingDelay]);

  const streamingContent = streamingReply?.content ?? '';
  // Identity, not the object: a tool result produces a fresh message object
  // with the same text, and restarting the timer there blanks the row for
  // 600ms at exactly the moment the tool label disappears.
  const streamingId = streamingReply?.id;
  const [isTextIdle, setIsTextIdle] = React.useState(false);

  React.useEffect(() => {
    if (!streamingId) {
      setIsTextIdle(false);
      return;
    }

    setIsTextIdle(false);
    const timer = setTimeout(() => setIsTextIdle(true), TEXT_IDLE_MS);

    return () => clearTimeout(timer);
  }, [streamingId, streamingContent]);

  const hasReadableStreamingText = Boolean(streamingContent.trim());
  const turnIsLive =
    isStreaming ?? (isAwaitingResponse || Boolean(streamingReply));
  const [isLongWait, setIsLongWait] = React.useState(false);
  React.useEffect(() => {
    if (!turnIsLive) {
      setIsLongWait(false);
      return;
    }
    const timer = setTimeout(() => setIsLongWait(true), STILL_WORKING_MS);
    return () => clearTimeout(timer);
  }, [turnIsLive, streamingContent]);

  const showReviewing = shouldShowReviewingStatus({
    turnHasToolActivity,
    replyIsStreaming: turnIsLive && Boolean(streamingReply),
    hasReadableText: hasReadableStreamingText,
    isTextIdle,
  });
  const waitLabel = isLongWait ? 'Still working...' : 'Thinking...';
  const activityLabel = activeToolCall
    ? toolCallLabel(activeToolCall, toolCallConfig, variant)
    : waitLabel;
  // The default surface shows its running tools in the message itself.
  const showActivityRow =
    variant === 'agent'
      ? turnIsLive &&
        Boolean(
          activeToolCall ||
            showReviewing ||
            (!turnHasToolActivity && (showThinking || isTextIdle)),
        )
      : showThinking;

  // Finished steps fold once the answer arrives; the message's steps button keeps them.
  const liveToolCalls =
    variant === 'agent' && turnIsLive && !hasReadableStreamingText
      ? (streamingReply?.toolCalls ?? NO_TOOL_CALLS)
      : NO_TOOL_CALLS;

  return {turnIsLive, showActivityRow, activityLabel, liveToolCalls};
}
